import React, { useEffect, useState } from 'react';
import { I18n } from '@kineticdata/react';
import {
  Card,
  CardCol,
  CardRow,
  InfoMessage,
  TimeAgo,
  C,
} from '@kineticdata/bundle-common';
import { parseActivityData } from '../../utils';
import classNames from 'classnames';

const MAX_CONTENT_LENGTH = 500;

const CommentNode = ({ className, comment, content, isOpen, toggle }) => {
  const contentLength = content['Details'].length;
  const isToggleable = contentLength > MAX_CONTENT_LENGTH;

  const metaRow = (
    <CardRow type="meta" className="text-muted">
      <span>
        <I18n>Created</I18n>{' '}
      </span>
      <strong>
        <TimeAgo timestamp={comment.createdAt} />
      </strong>
      {content['Submitted By'] && (
        <>
          <span>
            {' '}
            <I18n>by</I18n>{' '}
          </span>
          <strong>
            <I18n>{content['Submitted By']}</I18n>
          </strong>
        </>
      )}
    </CardRow>
  );

  const toggleMoreRow = isToggleable &&
    typeof toggle === 'function' && (
      <CardRow className="card__row--toggle">
        <button className="btn btn-text" onClick={toggle}>
          <span
            className={classNames('fa fa-fw fa-md text-muted', {
              'fa-plus': !isOpen,
              'fa-minus': isOpen,
            })}
          />
          <span>
            <I18n>{isOpen ? 'Show Less' : 'Show More'}</I18n>
          </span>
        </button>
      </CardRow>
    );

  const details =
    isToggleable && !isOpen
      ? content['Details'].substring(0, MAX_CONTENT_LENGTH).trim() + '...'
      : content['Details'];

  return (
    <li className={classNames('activity__row', className)}>
      <Card>
        <CardCol>
          <CardRow type="meta" className="text-muted">
            <CardCol>{metaRow}</CardCol>
            <CardCol className="card__col--append-meta">
              {toggleMoreRow}
            </CardCol>
          </CardRow>
          <CardRow className="comment-details">{details}</CardRow>
          {isToggleable && isOpen && toggleMoreRow}
        </CardCol>
      </Card>
    </li>
  );
};

const ToggleAllButton = ({ isOpen, toggle }) => (
  <button className="ml-auto btn btn-text" onClick={toggle}>
    <span
      className={classNames('fa fa-fw fa-md text-muted', {
        'fa-plus': !isOpen,
        'fa-minus': isOpen,
      })}
    />
    <span>
      <I18n>{!isOpen ? 'Expand All' : 'Collapse All'}</I18n>
    </span>
  </button>
);

export const CommentsList = ({ submission, handleAddComment }) => {
  const primarySubmission =
    submission &&
    ([C.REQUEST_FORM_TYPE, C.UTILITY_FORM_TYPE].includes(submission.type)
      ? submission
      : submission.parent);

  // Filter activities to comments only
  const comments = primarySubmission
    ? primarySubmission.activities.filter(a => a.type === 'Comment').reverse()
    : [];
  // Get count of comments
  const commentsCount = comments.length;
  // State for tracking which comments are expanded
  const [openCommentsList, setOpenCommentsList] = useState(
    new Array(commentsCount).fill(false),
  );
  // Size of state array for tracking expanded comments
  const openCommentsCount = openCommentsList.length;
  // Flag checking if all comments are expanded
  useEffect(
    () => {
      if (commentsCount > openCommentsCount) {
        setOpenCommentsList(list => [
          ...list,
          ...new Array(commentsCount - openCommentsCount).fill(false),
        ]);
      }
    },
    [commentsCount, openCommentsCount],
  );
  // Function that returns a handler for toggling an issue at a specific index
  const toggleComment = i => () =>
    setOpenCommentsList(list => {
      const toggledList = [...list];
      toggledList.splice(i, 1, !list[i]);
      return toggledList;
    });

  return (
    <ul className="activity activity--comments" role="tabpanel">
      {commentsCount > 0 ? (
        comments.map((comment, i) => (
          <CommentNode
            key={comment.id}
            comment={comment}
            content={parseActivityData(comment)}
            isOpen={!!openCommentsList[i]}
            toggle={toggleComment(i)}
          />
        ))
      ) : (
        <div className="text-muted">
          <InfoMessage title="No comments to display" />
        </div>
      )}
      {handleAddComment && (
        <>
          <li className="activity__spacer" aria-hidden="true" />
          <li className="activity__meta activity__meta--end">
            <button className="btn btn-tertiary" onClick={handleAddComment}>
              Add Comment
            </button>
          </li>
        </>
      )}
    </ul>
  );
};
